/**
 * The way back from the @yagni rail to a plan question.
 *
 * "Ask @yagni" on a plan question seeds the rail with the question. Whatever
 * the reader and @yagni settle there used to stay in the chat, and the reader
 * had to retype it into the question by hand. This is the return trip: the
 * rail hands a reply back as a window event, and the case file stages it the
 * same way it stages a typed answer. It still sends nothing; the answer rides
 * the dock's Approve or Send back like every other staged answer.
 *
 * An event rather than a callback because the rail and the case file are
 * siblings under the shell and neither should import the other.
 */
import { clipAtSentence } from "@yagni/shared";

/** The plan question a rail conversation was seeded from. */
export interface PlanQuestionRef {
  id: string;
  text: string;
}

export const PLAN_STAGE_ANSWER_EVENT = "plan.stageAnswer";

export interface PlanStageAnswerDetail {
  questionId: string;
  answer: string;
}

/** Well under the backend's 2000-character answer cap: an answer, not a transcript. */
export const MAX_RAIL_ANSWER = 1000;

/**
 * A reply as an answer: its words without the markdown marks, cut at a whole
 * sentence under the cap, else at a word with an ellipsis.
 */
export function answerFromReply(content: string, max = MAX_RAIL_ANSWER): string {
  const plain = content
    .replace(/```[^\n]*\n?/g, "")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/^>\s?/gm, "")
    .replace(/(\*\*|__)(.+?)\1/g, "$2")
    .replace(/(^|[^*\w])[*_]([^*_\n]+)[*_](?=[^*\w]|$)/g, "$1$2")
    // A link keeps its text; the URL may itself hold one level of parentheses.
    .replace(/\[([^\]]+)\]\((?:[^()\s]|\([^()\s]*\))+\)/g, "$1")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
  // A reply that was nothing but marks (a bare code fence) is handed over as
  // written rather than dropped: the reader asked for it and sees it staged.
  return clipAtSentence(plain || content.trim(), max);
}

/** Hand a rail reply to the plan question it answers. False when there is nothing to hand. */
export function stagePlanAnswerFromRail(questionId: string, reply: string): boolean {
  const answer = answerFromReply(reply);
  if (!answer) return false;
  window.dispatchEvent(
    new CustomEvent<PlanStageAnswerDetail>(PLAN_STAGE_ANSWER_EVENT, {
      detail: { questionId, answer },
    }),
  );
  return true;
}

/** Listen for rail replies handed to plan questions. Returns the unsubscribe. */
export function onPlanAnswerFromRail(
  handler: (detail: PlanStageAnswerDetail) => void,
): () => void {
  const listener = (event: Event) => {
    const detail = (event as CustomEvent<PlanStageAnswerDetail>).detail;
    if (!detail || typeof detail.questionId !== "string" || typeof detail.answer !== "string") {
      // The producer and this listener disagree about the event: say so, since
      // the rail has already told the reader the answer was staged.
      console.warn("[plan answers] ignored a plan.stageAnswer event with no usable detail", detail);
      return;
    }
    handler(detail);
  };
  window.addEventListener(PLAN_STAGE_ANSWER_EVENT, listener);
  return () => window.removeEventListener(PLAN_STAGE_ANSWER_EVENT, listener);
}
